import { useMemo, useState } from 'react';
import type { ParsedCSV, ShowcaseProspectRequest } from '../../../../types/extraction';
import type { ImportDuplicateFlag } from '../../../../types/api';
import { createTeamWithPlayers, createTeam, followTeam } from '../../../../services/teams';
import { checkImportDuplicates } from '../../../../services/prospects';
import { TeamTypeahead, type TeamOption } from '../../../../components/TeamTypeahead';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { readRosterClass, type JucoYear } from '../../../../utils/roster-class';
import {
  sanitizeValue,
  sanitizePhone,
  parseIntOrUndefined,
  sanitizeEmail,
  sanitizeHandedness,
  sanitizePosition,
  sanitizeHeight,
  sanitizeWeight,
  sanitizeGradYear,
} from '../../../../utils/data-sanitizer';
import { SentryService } from '../../../../services/sentry';
import { tourAnchor } from '../../../tours/tourAnchors';
import { useTour } from '../../../tours/useTour';
import { ROSTER_IMPORT_FEATURE, ROSTER_IMPORT_TOURS } from './rosterImportTours';
import { DuplicateImportGuard, type ImportResolution } from './DuplicateImportGuard';
import { toImportCandidate } from './importConflicts';
import { JucoYearReview } from './JucoYearReview';
import {
  buildJucoReviewRows,
  fillBlankJucoYears,
  JUCO_OVERWRITE_FIELDS,
  jucoYearsByRowIndex,
  stampJucoOnPlayer,
  unionOverwriteFields,
  unresolvedJucoRows,
  type JucoYearEdits,
} from './jucoImport';
import './TeamSelectionStep.css';

/**
 * A flagged candidate the user chose to "import as new". After a successful
 * import the parent uses this to resolve the newly-created prospect and open the
 * existing merge workflow against the existing match.
 */
export interface ImportAsNewCandidate {
  firstName: string;
  lastName: string;
  gradYear?: number | null;
  position?: string | null;
  /** The existing prospect this candidate matched — the merge "other" side. */
  existingMatchProspectId: string;
}

interface TeamSelectionStepProps {
  mappedData: ParsedCSV;
  /**
   * Seeds the JUCO toggle from the coach's up-front declaration on the upload-type step.
   *
   * Seed, not control: they can still untick it here. Read once as initial state rather than
   * held in sync, so a correction made on this screen is not undone by a re-render.
   */
  defaultIsJucoRoster?: boolean;
  /**
   * Originating UploadMetadata id of the roster image this import came from.
   * When present, the created/selected team's canonical roster image is
   * advanced to this upload so it can be viewed/annotated under the team.
   */
  rosterUploadId?: string;
  /**
   * The roster extraction (`roster_extractions.id`) these rows were reviewed from.
   * Sent with the commit so each imported prospect records where it came from.
   * Omitted when absent.
   */
  extractionId?: string;
  /**
   * Called on a successful import. `importAsNewCandidates` lists any flagged
   * players the user chose to import as new (empty when the guard never fired or
   * nothing was flagged) so the parent can offer the merge CTA.
   */
  onComplete: (
    teamId: string,
    teamName: string,
    prospectCount: number,
    importAsNewCandidates?: ImportAsNewCandidate[]
  ) => void;
  onBack: () => void;
}

/**
 * Map a mapped-CSV ParsedCSV into the sanitized `ShowcaseProspectRequest[]` the
 * team-creation endpoint expects. Index order is preserved so it aligns with the
 * duplicate-guard's `flag.index`.
 *
 * `jucoYears` is null unless the coach marked this roster JUCO. When it is set, every row is
 * stamped as a JUCO player at the year that row resolved to — keyed by row index, which is also
 * the payload index because this path imports every row rather than filtering any out.
 */
function buildPlayers(
  mappedData: ParsedCSV,
  jucoYears: Map<number, JucoYear> | null,
): ShowcaseProspectRequest[] {
  return mappedData.rows.map((row, rowIndex) => {
    const firstName = sanitizeValue(row.first_name);
    const lastName = sanitizeValue(row.last_name);
    const jerseyNumber = parseIntOrUndefined(row.number);
    const position = sanitizePosition(row.position);
    // One read of the cell, both facts kept: the eligibility year is what the roster said,
    // the grad year is inferred from it for the screens that have always shown one.
    const classReading = readRosterClass(row.class || row.grad_year);
    const gradYear = sanitizeGradYear(classReading.gradYear ?? undefined);
    const eligibilityClass = classReading.eligibilityClass ?? undefined;
    const email = sanitizeEmail(row.email);
    const phone = sanitizePhone(row.phone);
    const height = sanitizeHeight(row.height);
    const weight = parseIntOrUndefined(sanitizeWeight(row.weight));
    const bats = sanitizeHandedness(row.bats);
    const throws = sanitizeHandedness(row.throws);
    const school = sanitizeValue(row.school);

    const twitter = sanitizeValue(row.twitter);
    const instagram = sanitizeValue(row.instagram);
    const city = sanitizeValue(row.city);
    const state = sanitizeValue(row.state);
    const address = sanitizeValue(row.address);
    const zipCode = sanitizeValue(row.zip);
    const birthDate = sanitizeValue(row.birth_date);
    const parentName = sanitizeValue(row.parent_name);
    const parentPhone = sanitizePhone(row.parent_phone);
    const coachName = sanitizeValue(row.coach_name);
    const coachPhone = sanitizePhone(row.coach_phone);
    const advisorName = sanitizeValue(row.advisor_name);
    const advisorPhone = sanitizePhone(row.advisor_phone);
    const notes = sanitizeValue(row.notes);

    const player: ShowcaseProspectRequest = {
      firstName: firstName || 'Unknown', // firstName required
      lastName: lastName || 'Unknown', // lastName required
      jerseyNumber,
      position,
      gradYear,
      eligibilityClass,
      email,
      phone,
      height,
      weight,
      bats,
      throws,
      school,
      twitter,
      instagram,
      city,
      state,
      address,
      zipCode,
      birthDate,
      parentName,
      parentPhone,
      coachName,
      coachPhone,
      advisorName,
      advisorPhone,
      notes,
    };

    return jucoYears
      ? stampJucoOnPlayer(
          player,
          jucoYears.get(rowIndex) ?? null,
      // A stated grad year (a bare "2025") reads as a grad year and no class, so eligibilityClass
      // is null exactly when the roster said the year outright rather than us inferring it.
      { gradYearWasStated: classReading.eligibilityClass === null && classReading.gradYear != null },
        )
      : player;
  });
}

export function TeamSelectionStep({
  mappedData,
  rosterUploadId,
  extractionId,
  onComplete,
  onBack,
  defaultIsJucoRoster = false,
}: TeamSelectionStepProps) {
  const [selectedTeam, setSelectedTeam] = useState<TeamOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Follow the team as part of this step. Following surfaces the team's games
  // in the itinerary and does NOT require a roster — mirrors mobile's team
  // selection screen.
  const [shouldFollow, setShouldFollow] = useState(false);

  // "Follow only" means there's nothing to import — the user just wants to
  // track the team (no roster / no players required).
  const isFollowOnly = mappedData.rowCount === 0 && shouldFollow;

  // Duplicate-import guard state. When the pre-import check returns flags we
  // pause and show the guard; `guardFlags` is null the rest of the time so the
  // common no-duplicate path is unaffected.
  const [guardFlags, setGuardFlags] = useState<ImportDuplicateFlag[] | null>(null);

  // The JUCO toggle, and the JC years the coach filled in by hand. Scoped to this one team by
  // construction: this component only ever handles a single team, so there is no batch for the
  // toggle to leak across - see the note at the top of jucoImport.ts for why that matters.
  const [isJucoRosterTeam, setIsJucoRosterTeam] = useState(defaultIsJucoRoster);
  const [jucoEdits, setJucoEdits] = useState<JucoYearEdits>({});

  // Every row, named or not: this path imports them all (missing names default to "Unknown"), so
  // every row can be stamped and every row can block.
  const jucoReviewRows = useMemo(
    () => buildJucoReviewRows(mappedData.rows, { skipUnnamedRows: false }),
    [mappedData.rows],
  );

  const unresolvedJucoYearRows = isJucoRosterTeam
    ? unresolvedJucoRows(jucoReviewRows, jucoEdits)
    : [];
  const hasUnresolvedJucoYears = unresolvedJucoYearRows.length > 0;

  // Null rather than an empty map when the toggle is off, so buildPlayers stamps nothing at all
  // rather than stamping every row with no year.
  const jucoYearsForImport = useMemo(
    () => (isJucoRosterTeam ? jucoYearsByRowIndex(jucoReviewRows, jucoEdits) : null),
    [isJucoRosterTeam, jucoReviewRows, jucoEdits],
  );

  // The duplicate guard replaces the form, taking every anchor with it, and a submit in flight is no
  // moment to start explaining the form.
  useTour(ROSTER_IMPORT_FEATURE, {
    ready: !guardFlags && !isSubmitting,
    hasData: false,
    pageTourKey: ROSTER_IMPORT_TOURS.teamSelection,
    dataTourKey: null,
  });

  /**
   * Commit the import for the given players list. Shared by the no-flags path
   * and the guard-confirm path so both build the request and interpret the
   * response identically. `importAsNewCandidates` is threaded to onComplete for
   * the post-import merge CTA.
   */
  const commitImport = async (
    players: ShowcaseProspectRequest[],
    teamIdentifier: string,
    startTime: number,
    isNewTeam: boolean,
    importAsNewCandidates: ImportAsNewCandidate[]
  ) => {
    SentryService.addUploadBreadcrumb('Creating team with players', {
      team_identifier: teamIdentifier,
      player_count: players.length,
    });

    const response = await createTeamWithPlayers({
      team: teamIdentifier,
      players,
      // Only sent when truthy — an empty/absent id must not be sent (the
      // server would attempt to resolve an empty UploadMetadata token).
      ...(rosterUploadId ? { rosterUploadId } : {}),
      ...(extractionId ? { extractionId } : {}),
      // This step only follows an extraction (the upload wizard), so its players are always an
      // import, even when the coach edited rows during review. Sent even without an id so the
      // server never has to guess.
      entryMethod: 'extraction',
    });

    // Validate response
    if (!response || !response.teamId) {
      throw new Error('Invalid response from server: missing team ID');
    }

    if (typeof response.totalProspectsCreated !== 'number' || typeof response.totalProspectsLinked !== 'number') {
      throw new Error('Invalid response from server: missing prospect count');
    }

    // Total prospects = created + linked
    const totalProspects = response.totalProspectsCreated + response.totalProspectsLinked;

    SentryService.addUploadBreadcrumb('Team creation completed', {
      team_id: response.teamId,
      team_name: response.teamName,
      total_prospects: totalProspects,
      prospects_created: response.totalProspectsCreated,
      prospects_linked: response.totalProspectsLinked,
      is_new_team: isNewTeam,
      duration_ms: Date.now() - startTime,
    });

    // Following is a secondary action on the roster path — don't fail the
    // whole import if it doesn't take.
    if (shouldFollow) {
      try {
        await followTeam(response.teamId);
      } catch {
        /* non-blocking: the roster still imported */
      }
    }

    onComplete(
      response.teamId,
      response.teamName || teamIdentifier,
      totalProspects,
      importAsNewCandidates
    );
  };

  /**
   * Apply the guard's per-row decisions and commit the import.
   *
   * Merged players carry the existing prospect's id (and any fields the user
   * chose to take from the roster) so the server updates that record and adds it
   * to the team instead of creating a second one. Skipped players are dropped
   * from the list entirely; "import as new" players stay in untouched and feed
   * the post-import merge CTA. Unflagged players are always kept.
   */
  const handleGuardConfirm = async (resolutions: Record<string, ImportResolution>) => {
    if (!selectedTeam || !guardFlags) return;

    const startTime = Date.now();
    const isNewTeam = !selectedTeam.id;
    const teamIdentifier = selectedTeam.id || selectedTeam.name;

    // Rebuild players so the index still aligns with the flags we sent.
    const allPlayers = buildPlayers(mappedData, jucoYearsForImport);

    const skipIndices = new Set<number>();
    const importAsNewCandidates: ImportAsNewCandidate[] = [];
    for (const flag of guardFlags) {
      const resolution = resolutions[String(flag.index)];
      const decision = resolution?.decision ?? 'merge';

      if (decision === 'skip') {
        skipIndices.add(flag.index);
        continue;
      }

      if (decision === 'merge' && resolution?.matchProspectId) {
        // Union, not replace. buildPlayers may already have stamped this row with the two
        // fields the JUCO toggle owns, and assigning the guard's list over the top would drop
        // them - so a re-imported JUCO player would keep a stale HighSchool and the toggle would
        // look like it did nothing on exactly the rows a coach was resolving by hand.
        const stampedPlayer = allPlayers[flag.index];
        const overwriteFields = unionOverwriteFields(
          stampedPlayer.overwriteFields,
          resolution.overwriteFields,
        );

        allPlayers[flag.index] = {
          ...stampedPlayer,
          linkToProspectId: resolution.matchProspectId,
          ...(overwriteFields.length > 0 ? { overwriteFields } : {}),
        };
        continue;
      }

      // Import as new: keep the row as-is and remember the pairing so the parent
      // can offer a manual merge once the record exists.
      const match = flag.matches[0];
      if (match) {
        importAsNewCandidates.push({
          firstName: flag.candidate.firstName,
          lastName: flag.candidate.lastName,
          gradYear: flag.candidate.gradYear,
          position: flag.candidate.position,
          existingMatchProspectId: match.prospectId,
        });
      }
    }

    const reducedPlayers = allPlayers.filter((_, index) => !skipIndices.has(index));

    // If the user skipped everything, there is nothing to import — do NOT call
    // the API. Keep the guard open with a clear message so they can revise.
    if (reducedPlayers.length === 0) {
      setError('All flagged players were skipped and no other players remain to import.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await commitImport(reducedPlayers, teamIdentifier, startTime, isNewTeam, importAsNewCandidates);
    } catch (err) {
      SentryService.captureError('[UPLOAD FAILED] Team creation failed (post-guard)', {
        operation: 'team_selection',
        phase: 'creating_prospects',
        tags: {
          error_category: 'extraction_failed',
          upload_phase: 'team_upload',
          screen: 'TeamSelectionStep',
        },
        extra: {
          error: err instanceof Error ? err.message : String(err),
          team_name: selectedTeam.name,
          team_id: selectedTeam.id,
          is_new_team: isNewTeam,
          prospect_count: reducedPlayers.length,
          duration_ms: Date.now() - startTime,
        },
        fingerprint: ['roster-upload', 'team-creation', 'failed'],
      });
      setError(getSafeErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGuardCancel = () => {
    // Return to the team-selection form without committing anything.
    setGuardFlags(null);
    setError('');
  };

  const handleSubmit = async () => {
    // Validate team selection
    if (!selectedTeam) {
      setError('Please select a team');
      return;
    }

    if (mappedData.rowCount === 0 && !shouldFollow) {
      setError('This roster has no players. Turn on "Follow this team" to add it without a roster.');
      return;
    }

    const startTime = Date.now();
    const isNewTeam = !selectedTeam.id;

    // Set context for team selection
    SentryService.setUploadContext({
      trace_id: '', // Will be set by parent
      operation: 'team_selection',
      phase: 'creating_prospects',
      roster_type: 'single_team',
    });

    SentryService.addUploadBreadcrumb('Starting team selection', {
      team_name: selectedTeam.name,
      team_id: selectedTeam.id,
      is_new_team: isNewTeam,
      prospect_count: mappedData.rowCount,
    });

    setIsSubmitting(true);
    setError('');

    try {
      // Follow-only path: nothing to import. Resolve the team (an existing
      // selection, or create it by name with no players) and follow it. This
      // is the "just follow a team without a roster" capability.
      if (mappedData.rowCount === 0) {
        const teamId = selectedTeam.id ?? (await createTeam(selectedTeam.name)).id;
        await followTeam(teamId);
        onComplete(teamId, selectedTeam.name, 0);
        return;
      }

      // Use team ID if existing, otherwise use the name for new teams
      const teamIdentifier = selectedTeam.id || selectedTeam.name;

      // Convert mapped data to API format with sanitization. Index order is
      // preserved so it aligns with the duplicate-guard's `flag.index`.
      const players: ShowcaseProspectRequest[] = buildPlayers(mappedData, jucoYearsForImport);

      // Duplicate-import guard: BEFORE any records are created, check the
      // candidates against existing prospects. If nothing is flagged this is a
      // no-op and the import proceeds exactly as before.
      const { flagged } = await checkImportDuplicates({
        teamId: selectedTeam.id ?? null,
        players: players.map(toImportCandidate),
      });

      if (flagged.length > 0) {
        // Pause the import and surface the guard. Resolution continues in
        // handleGuardConfirm. Release the submitting state so the guard is
        // interactive.
        SentryService.addUploadBreadcrumb('Duplicate import guard triggered', {
          flagged_count: flagged.length,
        });
        setGuardFlags(flagged);
        setIsSubmitting(false);
        return;
      }

      // No duplicates — commit the full list unchanged (no merge candidates).
      await commitImport(players, teamIdentifier, startTime, isNewTeam, []);
    } catch (err) {
      const durationMs = Date.now() - startTime;

      SentryService.captureError('[UPLOAD FAILED] Team creation failed', {
        operation: 'team_selection',
        phase: 'creating_prospects',
        tags: {
          error_category: 'extraction_failed',
          upload_phase: 'team_upload',
          screen: 'TeamSelectionStep',
        },
        extra: {
          error: err instanceof Error ? err.message : String(err),
          team_name: selectedTeam.name,
          team_id: selectedTeam.id,
          is_new_team: isNewTeam,
          prospect_count: mappedData.rowCount,
          duration_ms: durationMs,
        },
        fingerprint: ['roster-upload', 'team-creation', 'failed'],
      });

      setError(getSafeErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // When the guard is active, replace the team-selection form with it. The
  // import stays paused here until the user resolves (confirm) or backs out.
  if (guardFlags) {
    return (
      <>
        <DuplicateImportGuard
          items={guardFlags.map((flag) => ({ id: String(flag.index), flag }))}
          onConfirm={handleGuardConfirm}
          onCancel={handleGuardCancel}
          isSubmitting={isSubmitting}
          // With the toggle on, the coach has already said what these two are. A stored
          // HighSchool beside an incoming JUCO is two non-blank values that disagree, which is
          // exactly what the guard counts as a conflict - so without this every row of every
          // re-import would ask them to confirm the toggle they just flipped.
          suppressedFields={isJucoRosterTeam ? JUCO_OVERWRITE_FIELDS : undefined}
        />
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="team-selection-step">
      <div className="team-selection-header">
        <h3>Select Team</h3>
        <p className="prospect-count">
          {mappedData.rowCount} prospect{mappedData.rowCount !== 1 ? 's' : ''} ready to import
        </p>
      </div>

      <div className="team-selection-content">
        <div className="form-field" {...tourAnchor('roster-import.team.search')}>
          <label htmlFor="team-selection-step-search-for-existing-team-or-create-0">Search for existing team or create new</label>
          <TeamTypeahead inputId="team-selection-step-search-for-existing-team-or-create-0"
            value={selectedTeam}
            onSelect={setSelectedTeam}
            placeholder="Start typing team name..."
            error={error && !selectedTeam ? error : undefined}
          />
        </div>

        <div className="form-field">
          <label className="follow-checkbox" {...tourAnchor('roster-import.team.follow')}>
            <input
              type="checkbox"
              checked={shouldFollow}
              onChange={(e) => setShouldFollow(e.target.checked)}
              disabled={isSubmitting}
            />
            <span>
              <strong>Follow this team</strong>
              <span className="toggle-text">
                {' '}— its games show in your itinerary, now and any added later. No roster required.
              </span>
            </span>
          </label>
        </div>

        {mappedData.rowCount > 0 && (
          <div className="form-field">
            <Checkbox
              checked={isJucoRosterTeam}
              onChange={(e) => setIsJucoRosterTeam(e.target.checked)}
              disabled={isSubmitting}
              label={
                <span>
                  <strong>This is a junior college roster</strong>
                  <span className="toggle-text">
                    {' '}— every player is stored as JUCO, at the JC year their Class column names.
                    This wins over a level already on file.
                  </span>
                </span>
              }
            />
          </div>
        )}

        {isJucoRosterTeam && (
          <JucoYearReview
            idPrefix="team-selection"
            rows={jucoReviewRows}
            edits={jucoEdits}
            onChangeRow={(rowIndex, year) =>
              setJucoEdits((current) => {
                const next = { ...current };
                if (year) next[rowIndex] = year;
                else delete next[rowIndex];
                return next;
              })
            }
            onFillBlanks={(year) =>
              setJucoEdits((current) => fillBlankJucoYears(jucoReviewRows, current, year))
            }
            disabled={isSubmitting}
          />
        )}
      </div>

      {error && selectedTeam && (
        <div className="error-message">{error}</div>
      )}

      {hasUnresolvedJucoYears && (
        // Blocking rather than importing half-stamped: a JUCO player with no JC year is the exact
        // state this feature exists to prevent. Named counts beat a greyed-out button with no
        // explanation, and the bulk fill above answers all of them at once.
        <div className="error-message">
          {unresolvedJucoYearRows.length === 1
            ? '1 player still needs a JC year'
            : `${unresolvedJucoYearRows.length} players still need a JC year`}{' '}
          before this roster can import. Set one above, or use Apply to fill them all at once.
        </div>
      )}

      <div className="button-group">
        <Button variant="secondary" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          {...tourAnchor('roster-import.team.submit')}
          disabled={
            isSubmitting ||
            (mappedData.rowCount === 0 && !shouldFollow) ||
            hasUnresolvedJucoYears
          }
        >
          {isSubmitting ? (
            <span className="button-content">
              <span className="spinner-small" />
              {isFollowOnly ? 'Following...' : 'Creating Prospects...'}
            </span>
          ) : isFollowOnly ? (
            'Follow Team'
          ) : shouldFollow ? (
            'Create & Follow'
          ) : (
            'Create Prospects'
          )}
        </Button>
      </div>
    </div>
  );
}
